import EventBus from "../modules/eventBus";
import StreamManager from "../modules/streamManager";
import LayoutManager from "../modules/layoutManager";
import MixEngine from "../modules/mixEngine";
import HotkeyController from "../modules/hotkeyController";
import { recordingStorage } from "../utils/recordingStorage";

const CAMERA_LIMIT = 2;
const AUTO_SWITCH_MIN_DEFAULT = 2; // minutes
const AUTO_SWITCH_MAX_DEFAULT = 5; // minutes
const AUTO_SWITCH_SPOTLIGHT_DEFAULT = 25; // seconds

export default class recorderClass {
  constructor() {
    if (recorderClass.instance) {
      return recorderClass.instance;
    }

    this.bus = new EventBus();
    this.layoutManager = new LayoutManager({ bus: this.bus });
    this.streamManager = new StreamManager({ bus: this.bus });
    this.mixEngine = new MixEngine({
      layoutManager: this.layoutManager,
      bus: this.bus,
    });
    this.hotkeys = new HotkeyController({
      bus: this.bus,
      layoutManager: this.layoutManager,
    });

    this.state = {
      mime:
        typeof MediaRecorder !== "undefined" &&
        MediaRecorder.isTypeSupported("video/mp4")
          ? "mp4"
          : "webm",
      filename: "",
      captureMode: null,
      isRecording: false,
      isPause: false,
      selectedCameras: [],
      selectedMicrophone: null,
      primaryCameraId: null,
      autoSwitchEnabled: false,
      autoSwitchMinMinutes: AUTO_SWITCH_MIN_DEFAULT,
      autoSwitchMaxMinutes: AUTO_SWITCH_MAX_DEFAULT,
      autoSwitchSpotlightSeconds: AUTO_SWITCH_SPOTLIGHT_DEFAULT,
      autoSwitchTimer: null,
      autoSwitchTimerEndsAt: null,
      autoSwitchSpotlightTimer: null,
      autoSwitchSpotlightEndsAt: null,
      autoSwitchMode: "idle",
      autoSwitchCurrentSecondary: null,
      autoSwitchLastSecondary: null,
    };

    this.audioContext = null;
    this.mixedAudioDestination = null;
    this.recordedChunks = [];
    this.currentSessionId = null;
    this.chunkIndex = 0;
    this.storageFailed = false;
    this.pendingChunkWrites = [];
    this.lastRecordingUrl = null;
    this.wakeLockSentinel = null;
    this.wakeLockRetryTimeout = null;
    this.wakeLockVisibilityListenerAttached = false;
    this.audioSources = [];
    this.cameraPreviewElements = new Map();
    this.primaryCameraPreview = null;
    this.recordingStartTime = null;
    this.recordingTimerInterval = null;
    this.autoSwitchCountdownInterval = null;

    this.set = {
      start: document.getElementById("start"),
      stop: document.getElementById("stop"),
      pauseAndResume: document.getElementById("pauseAndResume"),
      preview: document.querySelector("#preview"),
      download: document.querySelector("#download"),
      recordingName: document.querySelector("#filename"),
      videoWrapper: document.querySelector(".sh__video--wrp"),
      dropdownToggle: document.querySelector(".sh__dropdown--btn"),
      dropdownList: document.querySelector(".sh__dropdown__list"),
      dropdownDefaultOption: document.querySelector(
        ".sh__dropdown--defaultOption"
      ),
      dropdownOptions: document.querySelectorAll(".sh__dropdown__list--item"),
      dropdownChevron: document.querySelector(".sh__dropdown--icon.chevron"),
      headerText: document.querySelector(".sh__header"),
      toast: document.getElementById("toast"),
      cameraList: document.getElementById("cameraList"),
      cameraPreviewGrid: document.getElementById("cameraPreviewGrid"),
      cameraPrimaryFrame: document.querySelector(".sh__camera-primary-frame"),
      cameraStudio: document.getElementById("cameraStudio"),
      cameraStudioToggle: document.getElementById("cameraStudioToggle"),
      cameraStudioPanel: document.getElementById("cameraStudioPanel"),
      pipShapeControls: document.getElementById("pipShapeControls"),
      pipPositionControls: document.getElementById("pipPositionControls"),
      pipCornersToggle: document.getElementById("pipCornersToggle"),
      microphoneStudio: document.getElementById("microphoneStudio"),
      microphoneStudioToggle: document.getElementById("microphoneStudioToggle"),
      microphoneStudioPanel: document.getElementById("microphoneStudioPanel"),
      microphoneList: document.getElementById("microphoneList"),
      autoSwitchToggle: document.getElementById("autoSwitchToggle"),
      autoSwitchToggleWrapper: document.querySelector(
        'label.sh__toggle[for="autoSwitchToggle"]'
      ),
      autoSwitchMin: document.getElementById("autoSwitchMin"),
      autoSwitchMax: document.getElementById("autoSwitchMax"),
      autoSwitchDuration: document.getElementById("autoSwitchDuration"),
      autoSwitchCard: document.querySelector(".sh__studio-card--autoswitch"),
      cameraPreviewLegend: document.querySelector(".sh__camera-preview-legend"),
      cameraPreviewHint: document.querySelector(
        ".sh__studio-card__hint--preview"
      ),
      pipPreview: document.getElementById("pipPreview"),
      recordingInfo: document.querySelector(".sh__recording-info"),
      recordingTimer: document.querySelector(".sh__recording-timer__value"),
      recordingSwitch: document.querySelector(".sh__recording-switch"),
      recordingSwitchValue: document.querySelector(
        ".sh__recording-switch__value"
      ),
      recordingControls: document.querySelector(".sh__recording-controls"),
      reviewControls: document.querySelector(".sh__review-controls"),
      discardButton: document.getElementById("discard"),
      logo: document.querySelector(".sh__logo"),
      choice: document.querySelector(".sh__choice"),
      footer: document.querySelector(".sh__footer"),
      recoveryCard: document.getElementById("recoveryCard"),
      recoverBtn: document.getElementById("recoverBtn"),
      discardRecoveryBtn: document.getElementById("discardRecoveryBtn"),
      mimeChoiceWrapper: document.getElementById("mimeChoiceWrapper"),
      formatMp4: document.getElementById("formatMp4"),
      formatWebm: document.getElementById("formatWebm"),
      formatNotice: document.getElementById("formatNotice"),
    };

    this.handleDevicesUpdate = this.handleDevicesUpdate.bind(this);
    this.handleCameraSelectionChange =
      this.handleCameraSelectionChange.bind(this);
    this.handleAutoSwitchToggle = this.handleAutoSwitchToggle.bind(this);
    this.handleAutoSwitchMinChange = this.handleAutoSwitchMinChange.bind(this);
    this.handleAutoSwitchMaxChange = this.handleAutoSwitchMaxChange.bind(this);
    this.handleAutoSwitchDurationChange =
      this.handleAutoSwitchDurationChange.bind(this);
    this.handleCameraToggle = this.handleCameraToggle.bind(this);
    this.handleCaptureModeChange = this.handleCaptureModeChange.bind(this);
    this.handleMicrophoneToggle = this.handleMicrophoneToggle.bind(this);
    this.handleMicrophoneSelectionChange =
      this.handleMicrophoneSelectionChange.bind(this);
    this.handlePipShapeClick = this.handlePipShapeClick.bind(this);
    this.handlePipPositionClick = this.handlePipPositionClick.bind(this);
    this.handlePipCornersToggle = this.handlePipCornersToggle.bind(this);
    this.syncPipControls = this.syncPipControls.bind(this);
    this.handleWakeLockRelease = this.handleWakeLockRelease.bind(this);
    this.handleWakeLockVisibilityChange =
      this.handleWakeLockVisibilityChange.bind(this);
    this.beforeUnloadHandler = this.beforeUnloadHandler.bind(this);

    this.bus.on("devices:updated", this.handleDevicesUpdate);
    this.bus.on("hotkey:next-camera", () => this.cycleActiveCamera());
    this.bus.on("hotkey:primary-camera", () =>
      this.handlePrimaryCameraHotkey()
    );
    this.bus.on("hotkey:toggle-auto-switch", () =>
      this.handleAutoSwitchHotkey()
    );
    this.bus.on("layout:camera", () => this.renderCameraPreviewGrid());
    this.bus.on("layout:changed", () => this.renderCameraPreviewGrid());
    this.bus.on("layout:pip-config", this.syncPipControls);

    recorderClass.instance = this;
  }

  async init() {
    const permissionsReady = await this.ensureInitialPermissions();
    if (!permissionsReady) {
      return;
    }

    await this.streamManager.init();
    this.hotkeys.enable();
    this.layoutManager.setLayout("pip");
    this.layoutManager.setActiveCameraIndex(0);
    this.registerDropdown();
    this.registerFilenameInput();
    this.registerFormatSelector();
    this.registerCameraStudioControls();
    this.registerMicrophoneControls();
    this.registerPrimaryControls();
    this.registerLayoutControls();
    this.registerAutoSwitchControls();
    this.handleCaptureModeChange();
    this.syncPipControls(this.layoutManager.getPipConfig());
    await this.checkRecoveryOnStartup();
  }

  async ensureInitialPermissions() {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      return true;
    }

    const PERMISSION_STATUS_KEY = "screenrec:permissionsStatus";
    const PERMISSION_RELOAD_KEY = "screenrec:permissionsReload";

    let storage = null;
    try {
      storage = window?.sessionStorage ?? null;
    } catch (error) {
      console.debug("[PERMISSIONS] Session storage non disponibile", error);
    }

    if (storage?.getItem(PERMISSION_RELOAD_KEY) === "pending") {
      storage.setItem(PERMISSION_RELOAD_KEY, "done");
      return true;
    }

    const status = storage?.getItem(PERMISSION_STATUS_KEY);
    if (status === "granted" || status === "denied") {
      return true;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      stream.getTracks().forEach((track) => track.stop());
      if (storage) {
        storage.setItem(PERMISSION_STATUS_KEY, "granted");
        storage.setItem(PERMISSION_RELOAD_KEY, "pending");
      }
      window.location.reload();
      return false;
    } catch (error) {
      console.warn(
        "[PERMISSIONS] Impossibile ottenere i permessi iniziali",
        error
      );
      storage?.setItem(PERMISSION_STATUS_KEY, "denied");
      this.appendStatusNotification(
        "error",
        "Consenti l'accesso a webcam e microfono dalle impostazioni del browser per usare tutte le funzioni."
      );
      return true;
    }
  }

  registerFilenameInput() {
    const filenameInput = document.getElementById("filename");
    if (!filenameInput) return;

    if (!filenameInput.value) {
      const now = new Date();
      const dateStr = now.toLocaleDateString("it-IT").replace(/\//g, "-");
      const timeStr = now.toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      filenameInput.value = `Registrazione del ${dateStr} ore ${timeStr}`;
    }

    filenameInput.addEventListener("input", () => {
      const hasName = filenameInput.value.trim().length > 0;
      const hasMode = Boolean(this.state.captureMode);

      if (hasName && hasMode) {
        this.set.microphoneStudio?.classList.remove("force-hidden");
        this.set.cameraStudio?.classList.remove("force-hidden");
        this.set.start?.classList.remove("force-hidden");
        this.handleCaptureModeChange();
      } else {
        this.set.microphoneStudio?.classList.add("force-hidden");
        this.set.cameraStudio?.classList.add("force-hidden");
        this.set.start?.classList.add("force-hidden");
      }
    });

    // Make the filename field container visible if captureMode is already set
    if (this.state.captureMode) {
      this.set.recordingName.classList.add("visible");
    }
  }

  registerFormatSelector() {
    const mp4Supported =
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported("video/mp4");

    // --- Fallback se MP4 non è supportato (es. Firefox) ---
    if (!mp4Supported) {
      this.state.mime = "webm";

      if (this.set.formatMp4) {
        this.set.formatMp4.disabled = true;
        this.set.formatMp4.classList.remove("active");
        this.set.formatMp4.title = "MP4 non è supportato da questo browser";
      }
      if (this.set.formatWebm) {
        this.set.formatWebm.classList.add("active");
      }
      if (this.set.formatNotice) {
        this.set.formatNotice.textContent =
          "⚠️ MP4 non supportato: verrà usato WebM";
      }
    } else {
      // MP4 supportato: imposta lo stato visivo iniziale corretto (MP4 attivo)
      this.state.mime = "mp4";
      if (this.set.formatMp4) {
        this.set.formatMp4.classList.add("active");
      }
      if (this.set.formatWebm) {
        this.set.formatWebm.classList.remove("active");
      }
    }

    // --- Click listener pulsante MP4 ---
    this.set.formatMp4?.addEventListener("click", () => {
      if (this.set.formatMp4.disabled) return;
      this.state.mime = "mp4";
      this.set.formatMp4.classList.add("active");
      this.set.formatWebm?.classList.remove("active");
      if (this.set.formatNotice) this.set.formatNotice.textContent = "";
    });

    // --- Click listener pulsante WebM ---
    this.set.formatWebm?.addEventListener("click", () => {
      this.state.mime = "webm";
      this.set.formatWebm.classList.add("active");
      this.set.formatMp4?.classList.remove("active");
      if (this.set.formatNotice) this.set.formatNotice.textContent = "";
    });
  }

  registerDropdown() {
    if (!this.set.dropdownToggle) return;

    this.set.dropdownToggle.addEventListener("click", () => {
      this.toggleDropdown();
    });

    document.addEventListener("click", (e) => {
      if (this.set.dropdownToggle.classList.contains("toggled")) {
        if (!e.target.closest(".sh__dropdown--btn")) {
          this.toggleDropdown();
        }
      }
    });

    this.set.dropdownOptions.forEach((el) => {
      el.addEventListener("click", () => {
        this.set.recordingName.classList.add("visible");
        // Also show parent container/label if exists
        const container = this.set.recordingName.closest(
          ".sh__filename-container"
        );
        if (container) container.classList.add("visible");

        // Show format selector
        this.set.mimeChoiceWrapper?.classList.remove("force-hidden");
        this.set.mimeChoiceWrapper?.classList.add("visible");

        this.state.captureMode = this.getSelectedValue(el);
        this.toggleDropdown();
        this.handleCaptureModeChange();

        // Dispatch input event to immediately display studios/start button
        this.set.recordingName.dispatchEvent(new Event("input"));
      });
    });
  }

  registerCameraStudioControls() {
    if (!this.set.cameraStudio || !this.set.cameraStudioToggle) return;
    if (!this.set.cameraStudio.dataset.open) {
      this.set.cameraStudio.dataset.open = "false";
    }
    this.set.cameraStudioToggle.setAttribute("aria-expanded", "false");
    this.set.cameraStudioToggle.disabled = true;
    this.set.cameraStudioToggle.setAttribute("aria-disabled", "true");
    this.set.cameraStudioPanel?.setAttribute("aria-hidden", "true");
    this.set.cameraStudioToggle.addEventListener(
      "click",
      this.handleCameraToggle
    );

    const hasCameras = this.streamManager.getVideoInputs().length > 0;
    this.updateCameraStudioAvailability(hasCameras);
  }

  handleCameraToggle() {
    const isOpen = this.set.cameraStudio?.dataset.open === "true";
    this.setCameraStudioOpen(!isOpen);
  }

  updateCameraStudioAvailability(hasCameras) {
    if (!this.set.cameraStudio) return;
    const shouldShow = hasCameras && this.includesCameraSources();

    if (shouldShow) {
      this.set.cameraStudio.classList.remove("hidden");
      this.set.cameraStudio.removeAttribute("aria-hidden");
      if (this.set.cameraStudioToggle) {
        this.set.cameraStudioToggle.disabled = false;
        this.set.cameraStudioToggle.setAttribute("aria-disabled", "false");
      }
    } else {
      this.setCameraStudioOpen(false);
      this.set.cameraStudio.classList.add("hidden");
      this.set.cameraStudio.setAttribute("aria-hidden", "true");
      if (this.set.cameraStudioToggle) {
        this.set.cameraStudioToggle.disabled = true;
        this.set.cameraStudioToggle.setAttribute("aria-disabled", "true");
      }
    }
  }

  setCameraStudioOpen(isOpen) {
    if (!this.set.cameraStudio) return;
    this.set.cameraStudio.dataset.open = isOpen ? "true" : "false";
    this.set.cameraStudioToggle?.setAttribute("aria-expanded", String(isOpen));
    this.set.cameraStudioPanel?.setAttribute("aria-hidden", String(!isOpen));
  }

  clearCameraPreviews() {
    this.cameraPreviewElements.forEach((video) => {
      video.srcObject?.getTracks().forEach((track) => track.stop());
      video.remove();
    });
    this.cameraPreviewElements.clear();
    if (this.set.cameraPreviewGrid) {
      this.set.cameraPreviewGrid.innerHTML = `<p class="sh__studio-hint">Collega una webcam per iniziare.</p>`;
    }
    this.updatePrimaryCameraPreview();
  }

  clearSelectedCameras() {
    this.stopAutoSwitchTimer();
    this.state.autoSwitchEnabled = false;
    if (this.set.autoSwitchToggle) {
      this.set.autoSwitchToggle.checked = false;
    }
    this.state.selectedCameras.forEach((deviceId) =>
      this.streamManager.releaseCamera(deviceId)
    );
    this.state.selectedCameras = [];
    this.state.primaryCameraId = null;
    if (this.set.cameraList) {
      this.set.cameraList
        .querySelectorAll('input[type="checkbox"]')
        .forEach((box) => {
          box.checked = false;
        });
    }
    this.clearCameraPreviews();
  }

  ensurePrimaryCamera() {
    if (!this.state.selectedCameras.length) {
      this.state.primaryCameraId = null;
      this.updatePrimaryCameraPreview();
      return;
    }
    if (
      !this.state.primaryCameraId ||
      !this.state.selectedCameras.includes(this.state.primaryCameraId)
    ) {
      this.state.primaryCameraId = this.state.selectedCameras[0];
    }
    this.updatePrimaryCameraPreview();
  }

  getPrimaryCameraIndex() {
    if (!this.state.primaryCameraId) return -1;
    return this.state.selectedCameras.indexOf(this.state.primaryCameraId);
  }

  updateAutoSwitchUIState() {
    const hasCameraMode = this.includesCameraSources();
    const hasAnyCamera = this.state.selectedCameras.length > 0;
    const hasSecondary = this.hasSecondaryCameras();
    const canToggle = hasCameraMode && hasSecondary;
    const isMultiCam = hasCameraMode && hasSecondary;

    if (this.set.cameraStudio) {
      this.set.cameraStudio.dataset.multiWebcam = isMultiCam ? "true" : "false";
    }

    if (this.set.autoSwitchCard) {
      this.set.autoSwitchCard.style.display = isMultiCam ? "" : "none";
    }

    if (this.set.cameraPreviewLegend) {
      this.set.cameraPreviewLegend.style.display = isMultiCam ? "" : "none";
    }

    if (this.set.cameraPreviewHint) {
      this.set.cameraPreviewHint.style.display = isMultiCam ? "" : "none";
    }

    console.log("[AUTO-SWITCH] UI update:", {
      hasCameraMode,
      hasAnyCamera,
      hasSecondary,
      canToggle,
      isMultiCam,
      autoSwitchEnabled: this.state.autoSwitchEnabled,
      selectedCameras: this.state.selectedCameras.length,
      primaryCameraId: this.state.primaryCameraId,
    });

    if (!canToggle && this.state.autoSwitchEnabled) {
      console.warn("[AUTO-SWITCH] Force disabling due to conditions not met");
      this.state.autoSwitchEnabled = false;
      this.stopAutoSwitchTimer();
    }

    if (this.set.autoSwitchToggle) {
      this.set.autoSwitchToggle.disabled = !canToggle;
      this.set.autoSwitchToggle.checked = this.state.autoSwitchEnabled;
    }

    if (this.set.autoSwitchToggleWrapper) {
      const wrapperState = !hasCameraMode
        ? "camera-required"
        : hasSecondary
        ? "ready"
        : hasAnyCamera
        ? "needs-secondary"
        : "no-camera";
      this.set.autoSwitchToggleWrapper.classList.toggle(
        "is-disabled",
        !canToggle
      );
      this.set.autoSwitchToggleWrapper.dataset.state = wrapperState;
    }

    const disableInputs = !hasCameraMode || !hasSecondary;
    if (this.set.autoSwitchMin) {
      this.set.autoSwitchMin.disabled = disableInputs;
      if (!disableInputs) {
        this.set.autoSwitchMin.value = this.state.autoSwitchMinMinutes;
      }
    }
    if (this.set.autoSwitchMax) {
      this.set.autoSwitchMax.disabled = disableInputs;
      if (!disableInputs) {
        this.set.autoSwitchMax.value = this.state.autoSwitchMaxMinutes;
      }
    }
    if (this.set.autoSwitchDuration) {
      this.set.autoSwitchDuration.disabled = disableInputs;
      if (!disableInputs) {
        this.set.autoSwitchDuration.value =
          this.state.autoSwitchSpotlightSeconds;
      }
    }
  }

  resetMicrophoneSelection() {
    this.state.selectedMicrophone = null;
    if (this.set.microphoneList) {
      this.set.microphoneList
        .querySelectorAll('input[type="radio"]')
        .forEach((radio) => {
          radio.checked = false;
        });
    }
  }

  ensureDefaultMicrophoneSelection(audioInputs = []) {
    if (!this.includesMicrophoneSource()) return;
    const available = audioInputs.length
      ? audioInputs
      : this.streamManager.getAudioInputs();
    if (!available.length) return;

    const availableIds = available.map((d) => d.deviceId);
    if (
      !this.state.selectedMicrophone ||
      !availableIds.includes(this.state.selectedMicrophone)
    ) {
      this.state.selectedMicrophone = available[0].deviceId;
    }

    if (this.set.microphoneList) {
      const radio = this.set.microphoneList.querySelector(
        `input[type="radio"][value="${this.state.selectedMicrophone}"]`
      );
      if (radio) radio.checked = true;
    }
  }

  populateMicrophoneList(audioInputs = []) {
    if (!this.set.microphoneList) return;

    if (!audioInputs.length) {
      this.set.microphoneList.innerHTML = `<li class="sh__mic-list--empty">Nessun microfono rilevato</li>`;
      return;
    }

    if (
      this.includesMicrophoneSource() &&
      (!this.state.selectedMicrophone ||
        !audioInputs.some((d) => d.deviceId === this.state.selectedMicrophone))
    ) {
      this.state.selectedMicrophone = audioInputs[0].deviceId;
    }

    const selectedId = this.state.selectedMicrophone;
    this.set.microphoneList.innerHTML = audioInputs
      .map((device, index) => {
        const label = device.label || `Microfono ${index + 1}`;
        const isChecked = selectedId === device.deviceId ? "checked" : "";
        return `
          <li class="sh__mic-list--item">
            <label class="sh__device-card">
              <input type="radio" name="microphoneSource" value="${device.deviceId}" ${isChecked} />
              <span class="sh__device-card-indicator"></span>
              <span class="sh__device-card-icon">🎙️</span>
              <span class="sh__device-card-info">
                <span class="sh__device-card-title">${label}</span>
                <span class="sh__device-card-subtitle">Dispositivo audio</span>
              </span>
              <span class="sh__mic-level" aria-hidden="true"></span>
            </label>
          </li>
        `;
      })
      .join("");
  }

  syncMicrophoneSelection(audioInputs = []) {
    if (!this.set.microphoneList) return;
    if (!audioInputs.length) return;
    const selectedId = this.state.selectedMicrophone;
    if (!selectedId) return;
    const radio = this.set.microphoneList.querySelector(
      `input[type="radio"][value="${selectedId}"]`
    );
    if (radio) {
      radio.checked = true;
    }
  }
  includesCameraSources() {
    return this.state.captureMode === "screen-mic-cam";
  }

  includesMicrophoneSource() {
    return (
      this.state.captureMode === "screen-mic" ||
      this.state.captureMode === "screen-mic-cam"
    );
  }

  registerMicrophoneControls() {
    if (!this.set.microphoneStudio || !this.set.microphoneStudioToggle) return;
    if (!this.set.microphoneStudio.dataset.open) {
      this.set.microphoneStudio.dataset.open = "false";
    }
    this.set.microphoneStudioToggle.setAttribute("aria-expanded", "false");
    this.set.microphoneStudioToggle.disabled = true;
    this.set.microphoneStudioToggle.setAttribute("aria-disabled", "true");
    this.set.microphoneStudioPanel?.setAttribute("aria-hidden", "true");
    this.set.microphoneStudioToggle.addEventListener(
      "click",
      this.handleMicrophoneToggle
    );

    if (this.set.microphoneList) {
      this.set.microphoneList.addEventListener(
        "change",
        this.handleMicrophoneSelectionChange
      );
    }

    const audioInputs = this.streamManager.getAudioInputs();
    this.updateMicrophoneStudioAvailability(audioInputs);
  }

  handleMicrophoneToggle() {
    const isOpen = this.set.microphoneStudio?.dataset.open === "true";
    this.setMicrophoneStudioOpen(!isOpen);
  }

  updateMicrophoneStudioAvailability(input = []) {
    if (!this.set.microphoneStudio) return;

    let audioInputs = Array.isArray(input) ? input : [];
    const hasMicrophones = Array.isArray(input)
      ? input.length > 0
      : Boolean(input);

    if (!Array.isArray(input) && hasMicrophones) {
      audioInputs = this.streamManager.getAudioInputs();
    }

    const shouldShow = hasMicrophones && this.includesMicrophoneSource();

    if (shouldShow) {
      this.set.microphoneStudio.classList.remove("hidden");
      this.set.microphoneStudio.removeAttribute("aria-hidden");
      if (this.set.microphoneStudioToggle) {
        this.set.microphoneStudioToggle.disabled = false;
        this.set.microphoneStudioToggle.setAttribute("aria-disabled", "false");
      }
      this.populateMicrophoneList(audioInputs);
      this.ensureDefaultMicrophoneSelection(audioInputs);
      this.syncMicrophoneSelection(audioInputs);
    } else {
      this.setMicrophoneStudioOpen(false);
      this.set.microphoneStudio.classList.add("hidden");
      this.set.microphoneStudio.setAttribute("aria-hidden", "true");
      if (this.set.microphoneStudioToggle) {
        this.set.microphoneStudioToggle.disabled = true;
        this.set.microphoneStudioToggle.setAttribute("aria-disabled", "true");
      }
      this.populateMicrophoneList([]);
      if (!this.includesMicrophoneSource()) {
        this.resetMicrophoneSelection();
      }
    }
  }

  setMicrophoneStudioOpen(isOpen) {
    if (!this.set.microphoneStudio) return;
    this.set.microphoneStudio.dataset.open = isOpen ? "true" : "false";
    this.set.microphoneStudioToggle?.setAttribute(
      "aria-expanded",
      String(isOpen)
    );
    this.set.microphoneStudioPanel?.setAttribute(
      "aria-hidden",
      String(!isOpen)
    );
  }

  registerPrimaryControls() {
    this.set.start?.addEventListener("click", () => {
      if (!this.state.isRecording) {
        this.startRecording();
      }
    });

    this.set.pauseAndResume?.addEventListener("click", () => {
      if (!this.state.isPause) this.pauseRecording();
      else this.resumeRecording();
    });

    this.set.stop?.addEventListener("click", () => {
      if (this.state.isRecording) this.stopRecording();
    });

    this.set.discardButton?.addEventListener("click", () => {
      this.discardRecording();
    });

    this.registerRecoveryControls();
  }

  updatePauseResumeLabel(text) {
    const label = this.set.pauseAndResume?.querySelector("span");
    if (label) {
      label.textContent = text;
    }
  }

  registerLayoutControls() {
    if (this.set.pipShapeControls) {
      this.set.pipShapeControls.addEventListener(
        "click",
        this.handlePipShapeClick
      );
    }
    if (this.set.pipPositionControls) {
      this.set.pipPositionControls.addEventListener(
        "click",
        this.handlePipPositionClick
      );
    }
    if (this.set.pipCornersToggle) {
      this.set.pipCornersToggle.addEventListener(
        "change",
        this.handlePipCornersToggle
      );
    }
    if (this.set.cameraList) {
      this.set.cameraList.addEventListener(
        "change",
        this.handleCameraSelectionChange
      );
    }
  }

  handlePipShapeClick(event) {
    const button = event.target.closest("button[data-shape]");
    if (!button) return;
    event.preventDefault();
    const { shape } = button.dataset;
    this.layoutManager.setPipShape(shape);
    this.syncPipControls(this.layoutManager.getPipConfig());
  }

  handlePipPositionClick(event) {
    const button = event.target.closest("button[data-position]");
    if (!button) return;
    event.preventDefault();
    const { position } = button.dataset;
    this.layoutManager.setPipPosition(position);
    this.syncPipControls(this.layoutManager.getPipConfig());
  }

  handlePipCornersToggle(event) {
    const enabled = Boolean(event.target.checked);
    this.layoutManager.setPipRoundedCorners(enabled);
    this.syncPipControls(this.layoutManager.getPipConfig());
  }

  syncPipControls(config) {
    if (!config) return;
    if (this.set.pipShapeControls) {
      this.set.pipShapeControls
        .querySelectorAll("button[data-shape]")
        .forEach((btn) => {
          const isActive = btn.dataset.shape === config.shape;
          btn.classList.toggle("active", isActive);
          btn.setAttribute("aria-pressed", String(isActive));
        });
    }
    if (this.set.pipPositionControls) {
      this.set.pipPositionControls
        .querySelectorAll("button[data-position]")
        .forEach((btn) => {
          const isActive = btn.dataset.position === config.position;
          btn.classList.toggle("active", isActive);
          btn.setAttribute("aria-pressed", String(isActive));
        });
    }
    if (this.set.pipCornersToggle) {
      const shouldDisable = config.shape === "circle";
      this.set.pipCornersToggle.disabled = shouldDisable;
      this.set.pipCornersToggle.checked = Boolean(config.roundedCorners);
    }
    this.updatePipPreview(config);
    this.applyPipStylingToPreviews(config);
  }

  updatePipPreview(config) {
    if (!this.set.pipPreview || !config) return;
    const preview = this.set.pipPreview;
    const { shape, position, roundedCorners } = config;

    preview.classList.remove(
      "is-rectangle",
      "is-square",
      "no-rounded",
      "position-top-left",
      "position-top-right",
      "position-bottom-left",
      "position-bottom-right"
    );

    if (shape === "rectangle") {
      preview.classList.add("is-rectangle");
    } else if (shape === "square") {
      preview.classList.add("is-square");
    }

    if (!roundedCorners) {
      preview.classList.add("no-rounded");
    }

    switch (position) {
      case "top-left":
        preview.classList.add("position-top-left");
        break;
      case "top-right":
        preview.classList.add("position-top-right");
        break;
      case "bottom-left":
        preview.classList.add("position-bottom-left");
        break;
      default:
        preview.classList.add("position-bottom-right");
        break;
    }
  }

  applyPipStylingToPreviews(config) {
    if (!config || !this.set.cameraPreviewGrid) return;
    const cards = this.set.cameraPreviewGrid.querySelectorAll(
      ".sh__camera-preview-card"
    );
    cards.forEach((card) => {
      card.dataset.pipShape = config.shape;
      card.dataset.pipRounded =
        config.roundedCorners === false ? "flat" : "rounded";
    });
  }

  updatePrimaryCameraPreview() {
    if (!this.set.cameraPrimaryFrame) return;
    const container = this.set.cameraPrimaryFrame;
    const primaryId = this.state.primaryCameraId;
    const previewSource = primaryId
      ? this.cameraPreviewElements.get(primaryId)
      : null;

    if (
      !primaryId ||
      !previewSource ||
      (!previewSource.srcObject && !previewSource.src)
    ) {
      if (
        this.primaryCameraPreview &&
        container.contains(this.primaryCameraPreview)
      ) {
        this.primaryCameraPreview.pause?.();
        this.primaryCameraPreview.srcObject = null;
        this.primaryCameraPreview.removeAttribute("src");
        this.primaryCameraPreview.load?.();
        container.removeChild(this.primaryCameraPreview);
      } else {
        container.innerHTML = "";
      }
      return;
    }

    if (!this.primaryCameraPreview) {
      const video = document.createElement("video");
      video.className = "sh__camera-primary-video";
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      this.primaryCameraPreview = video;
    }

    if (!container.contains(this.primaryCameraPreview)) {
      container.innerHTML = "";
      container.appendChild(this.primaryCameraPreview);
    }

    if (
      previewSource.srcObject &&
      this.primaryCameraPreview.srcObject !== previewSource.srcObject
    ) {
      this.primaryCameraPreview.srcObject = previewSource.srcObject;
    } else if (
      !previewSource.srcObject &&
      previewSource.src &&
      this.primaryCameraPreview.src !== previewSource.src
    ) {
      this.primaryCameraPreview.src = previewSource.src;
    }

    this.primaryCameraPreview.play?.().catch(() => {});
  }

  registerAutoSwitchControls() {
    if (this.set.autoSwitchToggle) {
      this.set.autoSwitchToggle.addEventListener(
        "change",
        this.handleAutoSwitchToggle
      );
    }
    if (this.set.autoSwitchMin) {
      this.set.autoSwitchMin.addEventListener(
        "change",
        this.handleAutoSwitchMinChange
      );
    }
    if (this.set.autoSwitchMax) {
      this.set.autoSwitchMax.addEventListener(
        "change",
        this.handleAutoSwitchMaxChange
      );
    }
    if (this.set.autoSwitchDuration) {
      this.set.autoSwitchDuration.addEventListener(
        "change",
        this.handleAutoSwitchDurationChange
      );
    }
  }

  toggleDropdown() {
    this.set.dropdownToggle.classList.toggle("toggled");
    this.set.dropdownChevron.classList.toggle("toggled");
    this.set.dropdownList.classList.toggle("open");
  }

  getSelectedValue(el) {
    const selectedAttrValue = el.getAttribute("data-value");
    if (selectedAttrValue) {
      this.set.start.classList.add("visible");
    } else {
      this.set.start.classList.remove("visible");
    }
    this.set.dropdownDefaultOption.textContent = el.innerText;
    // NOTE: mime type is now managed independently by registerFormatSelector()
    // and should NOT be overridden here.
    return selectedAttrValue;
  }

  handleCaptureModeChange() {
    const videoInputs = this.streamManager.getVideoInputs();
    const audioInputs = this.streamManager.getAudioInputs();
    const hasVideoInputs = videoInputs.length > 0;
    const hasAudioInputs = audioInputs.length > 0;

    if (this.includesCameraSources()) {
      this.updateCameraStudioAvailability(hasVideoInputs);
      if (!hasVideoInputs) {
        this.clearSelectedCameras();
      } else {
        this.ensureDefaultCameraSelection(videoInputs);
        this.syncCameraSelectionControls();
        this.prepareCameraPreviews().catch((error) => {
          console.warn("Impossibile preparare l'anteprima webcam", error);
        });
      }
    } else {
      if (this.state.selectedCameras.length) {
        this.clearSelectedCameras();
      } else {
        this.clearCameraPreviews();
      }
      this.updateCameraStudioAvailability(false);
    }

    if (this.includesMicrophoneSource()) {
      this.updateMicrophoneStudioAvailability(audioInputs);
      if (hasAudioInputs) {
        this.ensureDefaultMicrophoneSelection(audioInputs);
      }
    } else {
      this.updateMicrophoneStudioAvailability([]);
      this.resetMicrophoneSelection();
    }

    const cameraControlsEnabled = this.includesCameraSources();
    if (this.set.autoSwitchToggle) {
      this.set.autoSwitchToggle.disabled = !cameraControlsEnabled;
    }
    this.updateAutoSwitchUIState();
  }

  handleAutoSwitchToggle(event) {
    const enabled = event.target.checked;
    console.log("[AUTO-SWITCH] Toggle fired:", {
      enabled,
      selectedCameras: this.state.selectedCameras,
      primaryCameraId: this.state.primaryCameraId,
      currentAutoSwitchState: this.state.autoSwitchEnabled,
      includesCameraSources: this.includesCameraSources(),
      hasSecondaryCameras: this.hasSecondaryCameras(),
    });

    if (enabled && this.state.selectedCameras.length <= 1) {
      console.warn("[AUTO-SWITCH] Not enough cameras, aborting");
      event.target.checked = false;
      this.state.autoSwitchEnabled = false;
      this.appendStatusNotification(
        "error",
        "Seleziona almeno due webcam per usare il cambio automatico"
      );
      return;
    }

    this.state.autoSwitchEnabled = enabled;
    console.log(
      "[AUTO-SWITCH] State updated to:",
      this.state.autoSwitchEnabled
    );

    if (enabled) {
      console.log("[AUTO-SWITCH] Starting cycle");
      this.ensurePrimaryCamera();
      this.activatePrimaryCamera();
      this.startAutoSwitchTimer();
      if (this.state.isRecording) {
        this.startAutoSwitchCountdown();
      }
    } else {
      console.log("[AUTO-SWITCH] Stopping cycle");
      this.stopAutoSwitchTimer();
      this.activatePrimaryCamera();
      if (this.state.isRecording) {
        this.stopAutoSwitchCountdown();
      }
    }

    this.updateAutoSwitchUIState();
  }

  handleAutoSwitchMinChange(event) {
    const value = Math.max(
      1,
      Number(event.target.value) || AUTO_SWITCH_MIN_DEFAULT
    );
    this.state.autoSwitchMinMinutes = value;
    if (this.state.autoSwitchMaxMinutes < value) {
      this.state.autoSwitchMaxMinutes = value;
      if (this.set.autoSwitchMax) {
        this.set.autoSwitchMax.value = value;
      }
    }
    this.restartAutoSwitchCycle({ preserveActive: true });
  }

  handleAutoSwitchMaxChange(event) {
    const value = Math.max(
      1,
      Number(event.target.value) || AUTO_SWITCH_MAX_DEFAULT
    );
    this.state.autoSwitchMaxMinutes = Math.max(
      value,
      this.state.autoSwitchMinMinutes
    );
    if (this.set.autoSwitchMax) {
      this.set.autoSwitchMax.value = this.state.autoSwitchMaxMinutes;
    }
    this.restartAutoSwitchCycle({ preserveActive: true });
  }

  handleAutoSwitchDurationChange(event) {
    const value = Math.max(
      5,
      Number(event.target.value) || AUTO_SWITCH_SPOTLIGHT_DEFAULT
    );
    this.state.autoSwitchSpotlightSeconds = value;
    this.restartAutoSwitchCycle({ preserveActive: false });
  }

  handlePrimaryCameraHotkey() {
    if (!this.state.selectedCameras.length) return;
    this.ensurePrimaryCamera();
    this.activatePrimaryCamera();
    if (this.state.autoSwitchEnabled) {
      this.restartAutoSwitchCycle({ preserveActive: false });
    }
  }

  handleAutoSwitchHotkey() {
    if (!this.set.autoSwitchToggle) return;
    if (this.set.autoSwitchToggle.disabled) {
      const message = this.includesCameraSources()
        ? "Seleziona due webcam per usare il cambio automatico"
        : "Passa alla modalità con webcam per attivare il cambio automatico";
      this.appendStatusNotification("error", message);
      return;
    }
    const toggle = this.set.autoSwitchToggle;
    toggle.checked = !toggle.checked;
    toggle.dispatchEvent(new Event("change", { bubbles: true }));
  }

  async handleDevicesUpdate(devices) {
    const videoInputs = devices.filter(
      (device) => device.kind === "videoinput"
    );
    const audioInputs = devices.filter(
      (device) => device.kind === "audioinput"
    );
    const availableIds = videoInputs.map((device) => device.deviceId);

    const removedSelections = this.state.selectedCameras.filter(
      (id) => !availableIds.includes(id)
    );
    removedSelections.forEach((deviceId) => {
      const preview = this.cameraPreviewElements.get(deviceId);
      if (preview) {
        preview.srcObject?.getTracks().forEach((track) => track.stop());
        preview.remove();
        this.cameraPreviewElements.delete(deviceId);
      }
      this.streamManager.releaseCamera(deviceId);
    });

    this.state.selectedCameras = this.state.selectedCameras.filter((id) =>
      availableIds.includes(id)
    );

    if (
      this.state.primaryCameraId &&
      !availableIds.includes(this.state.primaryCameraId)
    ) {
      this.state.primaryCameraId = null;
    }

    if (this.set.cameraList) {
      if (!videoInputs.length) {
        this.updateCameraStudioAvailability(false);
        this.set.cameraList.innerHTML = `<li class="sh__cam-list--empty">Nessuna webcam rilevata</li>`;
        this.clearCameraPreviews();
        this.stopAutoSwitchTimer();
      } else {
        this.updateCameraStudioAvailability(true);
        this.ensureDefaultCameraSelection(videoInputs);

        this.set.cameraList.innerHTML = videoInputs
          .map(
            (device) => `
              <li class="sh__cam-list--item">
                <label class="sh__device-card">
                  <input type="checkbox" value="${device.deviceId}" />
                  <span class="sh__device-card-indicator"></span>
                  <span class="sh__device-card-icon">📹</span>
                  <span class="sh__device-card-info">
                    <span class="sh__device-card-title">${
                      device.label || "Webcam"
                    }</span>
                    <span class="sh__device-card-subtitle">Dispositivo video</span>
                  </span>
                </label>
              </li>
            `
          )
          .join("");

        this.state.selectedCameras.forEach((deviceId) => {
          const checkbox = this.set.cameraList.querySelector(
            `input[type="checkbox"][value="${deviceId}"]`
          );
          if (checkbox) checkbox.checked = true;
        });

        if (this.state.selectedCameras.length) {
          await this.prepareCameraPreviews();
          if (this.state.autoSwitchEnabled) {
            this.startAutoSwitchTimer();
          }
        } else {
          this.renderCameraPreviewGrid();
        }
      }
    }

    const audioIds = audioInputs.map((device) => device.deviceId);
    if (
      this.state.selectedMicrophone &&
      !audioIds.includes(this.state.selectedMicrophone)
    ) {
      this.state.selectedMicrophone = null;
    }
    this.updateMicrophoneStudioAvailability(audioInputs);
    if (audioInputs.length) {
      this.ensureDefaultMicrophoneSelection(audioInputs);
    }

    this.ensurePrimaryCamera();
    this.updateAutoSwitchUIState();
  }

  ensureDefaultCameraSelection(videoInputs = []) {
    if (!this.includesCameraSources()) return;
    const available = videoInputs.length
      ? videoInputs
      : this.streamManager.getVideoInputs();
    if (!available.length) return;

    const availableIds = available.map((device) => device.deviceId);
    const validSelected = this.state.selectedCameras.filter((id) =>
      availableIds.includes(id)
    );

    if (validSelected.length > 0) {
      this.state.selectedCameras = validSelected;
      if (
        !this.state.primaryCameraId ||
        !validSelected.includes(this.state.primaryCameraId)
      ) {
        this.state.primaryCameraId = validSelected[0];
      }
    } else {
      const firstCamera = available[0];
      this.state.selectedCameras = [firstCamera.deviceId];
      this.state.primaryCameraId = firstCamera.deviceId;
      this.layoutManager.setActiveCameraIndex(0);
    }
  }

  syncCameraSelectionControls() {
    if (!this.set.cameraList) return;
    this.set.cameraList
      .querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = this.state.selectedCameras.includes(checkbox.value);
      });
  }

  async handleCameraSelectionChange(event) {
    if (!event.target.matches('input[type="checkbox"]')) return;

    const checkboxes = [
      ...this.set.cameraList.querySelectorAll('input[type="checkbox"]'),
    ];
    const selected = [];
    checkboxes.forEach((box) => {
      if (box.checked) {
        if (selected.length < CAMERA_LIMIT) {
          selected.push(box.value);
        } else {
          box.checked = false;
        }
      }
    });

    if (this.includesCameraSources() && selected.length === 0) {
      event.target.checked = true;
      this.appendStatusNotification(
        "error",
        "Almeno una webcam deve rimanere selezionata"
      );
      return;
    }

    const removed = this.state.selectedCameras.filter(
      (id) => !selected.includes(id)
    );
    removed.forEach((deviceId) => {
      const preview = this.cameraPreviewElements.get(deviceId);
      if (preview) {
        preview.srcObject?.getTracks().forEach((track) => track.stop());
        preview.remove();
        this.cameraPreviewElements.delete(deviceId);
      }
      this.streamManager.releaseCamera(deviceId);
    });

    this.state.selectedCameras = selected;

    const activeIndex = this.layoutManager.getActiveCameraIndex();
    if (activeIndex >= this.state.selectedCameras.length) {
      this.layoutManager.setActiveCameraIndex(
        Math.max(0, this.state.selectedCameras.length - 1)
      );
    }

    if (!this.state.selectedCameras.length) {
      this.stopAutoSwitchTimer();
      this.renderCameraPreviewGrid();
      this.setCameraStudioOpen(false);
      this.ensurePrimaryCamera();
      this.updateAutoSwitchUIState();
      return;
    }

    await this.prepareCameraPreviews();
    this.setCameraStudioOpen(true);
    this.ensurePrimaryCamera();
    this.updateAutoSwitchUIState();
    if (this.state.autoSwitchEnabled) {
      this.startAutoSwitchTimer();
    }
  }

  handleMicrophoneSelectionChange(event) {
    if (!event.target.matches('input[type="radio"]')) return;
    if (this.includesMicrophoneSource() && !event.target.value) {
      if (this.state.selectedMicrophone) {
        const radio = this.set.microphoneList?.querySelector(
          `input[type="radio"][value="${this.state.selectedMicrophone}"]`
        );
        if (radio) radio.checked = true;
      }
      this.appendStatusNotification(
        "error",
        "Almeno un microfono deve rimanere selezionato"
      );
      return;
    }
    this.state.selectedMicrophone = event.target.value || null;
  }

  async prepareCameraPreviews() {
    if (!this.set.cameraPreviewGrid) return;
    const streams = await Promise.all(
      this.state.selectedCameras.map((deviceId) =>
        this.streamManager.getCameraStream(deviceId).catch((error) => {
          console.error("Camera stream error", error);
          return null;
        })
      )
    );

    this.state.selectedCameras.forEach((deviceId, index) => {
      const stream = streams[index];
      if (!stream) return;
      let preview = this.cameraPreviewElements.get(deviceId);
      if (!preview) {
        preview = document.createElement("video");
        preview.className = "sh__camera-preview";
        preview.muted = true;
        preview.autoplay = true;
        preview.playsInline = true;
        this.cameraPreviewElements.set(deviceId, preview);
      }
      preview.srcObject = stream;
    });

    this.renderCameraPreviewGrid();
  }

  renderCameraPreviewGrid() {
    if (!this.set.cameraPreviewGrid) return;
    if (this.state.selectedCameras.length <= 1) {
      this.set.cameraPreviewGrid.innerHTML = "";
      this.set.cameraPreviewGrid.style.display = "none";
      this.updatePrimaryCameraPreview();
      return;
    }
    this.set.cameraPreviewGrid.style.display = "";
    this.set.cameraPreviewGrid.innerHTML = "";
    this.state.selectedCameras.forEach((deviceId, index) => {
      const video = this.cameraPreviewElements.get(deviceId);
      if (!video) return;
      const wrapper = document.createElement("div");
      wrapper.className = "sh__camera-preview-card";
      wrapper.dataset.camera = deviceId;
      const pipConfig = this.layoutManager.getPipConfig?.() || {};
      wrapper.dataset.pipShape = pipConfig.shape || "circle";
      wrapper.dataset.pipRounded =
        pipConfig.roundedCorners === false ? "flat" : "rounded";
      const isActive = index === this.layoutManager.getActiveCameraIndex();
      const isPrimary = deviceId === this.state.primaryCameraId;
      const badgeClasses = ["sh__camera-preview__badge"];
      if (isActive) {
        badgeClasses.push("active");
      } else if (isPrimary) {
        badgeClasses.push("primary");
      }
      const badgeLabel = isActive
        ? "Attiva"
        : isPrimary
        ? "Primaria"
        : "Anteprima";
      wrapper.classList.toggle("is-active", isActive);
      wrapper.classList.toggle("is-primary", isPrimary);

      wrapper.innerHTML = `
        <div class="${badgeClasses.join(" ")}">
          ${badgeLabel}
        </div>
      `;
      wrapper.appendChild(video);
      wrapper.addEventListener("click", () => {
        const clickedIndex = this.state.selectedCameras.indexOf(deviceId);
        if (clickedIndex >= 0) {
          this.state.primaryCameraId = deviceId;
          this.state.autoSwitchLastSecondary = null;
          this.layoutManager.setActiveCameraIndex(clickedIndex);
          this.renderCameraPreviewGrid();
          if (this.state.autoSwitchEnabled) {
            this.restartAutoSwitchCycle({ preserveActive: false });
          }
        }
      });
      this.set.cameraPreviewGrid.appendChild(wrapper);
    });
    this.updatePrimaryCameraPreview();
  }

  async startRecording() {
    if (!this.state.captureMode) {
      this.appendStatusNotification(
        "error",
        "Scegli cosa registrare prima di iniziare"
      );
      console.warn("Nessuna modalità di registrazione selezionata");
      return;
    }

    if (this.includesMicrophoneSource() && !this.state.selectedMicrophone) {
      this.appendStatusNotification(
        "error",
        "Seleziona un microfono prima di iniziare la registrazione"
      );
      return;
    }

    if (
      this.includesCameraSources() &&
      this.state.selectedCameras.length === 0
    ) {
      this.appendStatusNotification(
        "error",
        "Seleziona una videocamera prima di iniziare la registrazione"
      );
      return;
    }

    const layout = this.layoutManager.getActiveLayout();
    const requiredCameras = layout?.requires?.cameras ?? 0;
    if (this.includesCameraSources() && requiredCameras > 0) {
      if (this.state.selectedCameras.length < requiredCameras) {
        this.appendStatusNotification(
          "error",
          `Seleziona almeno ${requiredCameras} webcam per questo layout`
        );
        console.warn("Layout requires more webcam sources");
        return;
      }
    }
    try {
      this.state.filename = document.getElementById("filename")?.value || "";
      this.state.isRecording = true;
      this.state.isPause = false;
      this.recordedChunks = [];
      if (this.lastRecordingUrl) {
        URL.revokeObjectURL(this.lastRecordingUrl);
        this.lastRecordingUrl = null;
      }

      this.enableWakeLockHandling();

      this.currentSessionId = null;
      this.chunkIndex = 0;
      this.storageFailed = false;
      this.pendingChunkWrites = [];
      this.currentSessionId = await recordingStorage.startNewSession(
        this.state.mime,
        this.state.filename
      );
      this.storageFailed = !this.currentSessionId;

      // Register beforeunload event listener to prevent tab closure
      window.addEventListener("beforeunload", this.beforeUnloadHandler);

      const { composedStream, previewStream } =
        await this.prepareRecordingStreams();

      this.setupMediaRecorder(composedStream);

      if (this.set.preview) {
        if (this.set.preview.src && this.set.preview.src.startsWith("blob:")) {
          URL.revokeObjectURL(this.set.preview.src);
        }
        this.set.preview.removeAttribute("src");
        this.set.preview.srcObject = previewStream;
        this.set.preview.controls = false;
        this.set.preview.muted = true;
        this.set.preview.classList.add("visible");
      }

      // Hide all UI elements except video and recording controls
      this.set.logo?.classList.add("force-hidden");
      this.set.headerText?.classList.add("force-hidden");
      this.set.choice?.classList.add("force-hidden");
      this.set.mimeChoiceWrapper?.classList.add("force-hidden");
      this.set.mimeChoiceWrapper?.classList.remove("visible");
      this.set.microphoneStudio?.classList.add("hidden", "force-hidden");
      this.set.cameraStudio?.classList.add("hidden", "force-hidden");
      this.set.start?.classList.add("force-hidden");
      this.set.footer?.classList.add("force-hidden");

      // Show recording info and controls
      this.set.recordingInfo?.classList.add("visible");
      this.set.recordingControls?.classList.add("visible");
      this.set.pauseAndResume?.classList.add("visible");
      this.set.stop?.classList.add("visible");

      // Hide review controls
      if (this.set.reviewControls) {
        this.set.reviewControls.classList.remove("visible");
        this.set.reviewControls.setAttribute("aria-hidden", "true");
        this.set.reviewControls.style.setProperty("display", "none");
      }
      if (this.set.download) {
        if (
          this.set.download.href &&
          this.set.download.href.startsWith("blob:")
        ) {
          URL.revokeObjectURL(this.set.download.href);
        }
        this.set.download.href = "";
        this.set.download.removeAttribute("download");
        this.set.download.classList.remove("visible");
        this.set.download.setAttribute("disabled", "true");
      }
      if (this.set.discardButton) {
        this.set.discardButton.setAttribute("disabled", "true");
        this.set.discardButton.classList.remove("visible");
      }

      this.set.pauseAndResume?.classList.add("pause");
      this.set.pauseAndResume?.classList.remove("resume");
      this.updatePauseResumeLabel("Pausa");

      // Start recording timer
      this.startRecordingTimer();

      // Start auto-switch countdown if enabled
      if (this.state.autoSwitchEnabled) {
        this.startAutoSwitchCountdown();
      } else {
        this.set.recordingSwitch?.style.setProperty("display", "none");
        this.refreshAutoSwitchCountdownDisplay();
      }

      this.appendStatusNotification("start");
    } catch (error) {
      console.error("Failed to start recording", error);
      alert(
        "Errore avvio registrazione:\n" +
          error.message +
          "\n\nStack:\n" +
          error.stack
      );
      this.appendStatusNotification(
        "error",
        "Impossibile avviare la registrazione: " + error.message
      );
      this.state.isRecording = false;
      this.disableWakeLockHandling();
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }
  }

  async prepareRecordingStreams() {
    const needsMicrophone = this.includesMicrophoneSource();
    const captureSystemAudio = true;

    const screenStream = await this.streamManager.getScreenStream({
      audio: captureSystemAudio,
    });

    let microphoneStream = null;
    if (needsMicrophone) {
      try {
        microphoneStream = await this.streamManager.getMicrophoneStream({
          deviceId: this.state.selectedMicrophone,
        });
      } catch (error) {
        console.error("Unable to access microphone", error);
        this.appendStatusNotification(
          "error",
          "Impossibile accedere al microfono selezionato"
        );
        microphoneStream = null;
      }
    }

    const cameraStreams = await Promise.all(
      this.state.selectedCameras.map((deviceId) =>
        this.streamManager.getCameraStream(deviceId).catch((error) => {
          console.error("Unable to start camera", error);
          return null;
        })
      )
    );

    const filteredCameraStreams = cameraStreams.filter(Boolean);

    this.mixEngine.setScreenStream(screenStream);
    this.mixEngine.setCameraStreams(filteredCameraStreams);

    const previewStream = this.mixEngine.start({ frameRate: 30 });

    const audioTracks = [];
    if (screenStream?.getAudioTracks().length) {
      audioTracks.push(...screenStream.getAudioTracks());
    }
    if (microphoneStream?.getAudioTracks().length) {
      audioTracks.push(...microphoneStream.getAudioTracks());
    }

    const audioMixStream = await this.mixAudioTracks(audioTracks);

    const composedStream = new MediaStream([
      ...previewStream.getVideoTracks(),
      ...(audioMixStream ? audioMixStream.getAudioTracks() : []),
    ]);

    this.activeStreams = {
      screenStream,
      microphoneStream,
      cameraStreams: filteredCameraStreams,
      previewStream,
      audioMixStream,
    };

    if (this.state.autoSwitchEnabled) {
      this.startAutoSwitchTimer();
    }

    return { composedStream, previewStream };
  }

  supportsWakeLock() {
    return (
      typeof navigator !== "undefined" &&
      !!navigator.wakeLock &&
      typeof navigator.wakeLock.request === "function"
    );
  }

  enableWakeLockHandling() {
    if (!this.supportsWakeLock()) return;
    if (
      !this.wakeLockVisibilityListenerAttached &&
      typeof document !== "undefined"
    ) {
      document.addEventListener(
        "visibilitychange",
        this.handleWakeLockVisibilityChange
      );
      this.wakeLockVisibilityListenerAttached = true;
    }
    this.clearWakeLockRetry();
    this.acquireWakeLock();
  }

  disableWakeLockHandling() {
    this.clearWakeLockRetry();
    this.releaseWakeLock();
    if (
      this.wakeLockVisibilityListenerAttached &&
      typeof document !== "undefined"
    ) {
      document.removeEventListener(
        "visibilitychange",
        this.handleWakeLockVisibilityChange
      );
      this.wakeLockVisibilityListenerAttached = false;
    }
  }

  async acquireWakeLock() {
    if (!this.supportsWakeLock()) return;
    if (!this.state.isRecording) return;
    if (this.wakeLockSentinel) return;
    if (!this.isDocumentVisible()) {
      return;
    }
    try {
      this.wakeLockSentinel = await navigator.wakeLock.request("screen");
      this.wakeLockSentinel.addEventListener(
        "release",
        this.handleWakeLockRelease
      );
    } catch (error) {
      const hiddenForPolicy = !this.isDocumentVisible();
      if (!hiddenForPolicy) {
        console.warn("Unable to acquire wake lock", error);
        this.scheduleWakeLockRetry();
      }
    }
  }

  async releaseWakeLock() {
    if (!this.wakeLockSentinel) return;
    try {
      this.wakeLockSentinel.removeEventListener(
        "release",
        this.handleWakeLockRelease
      );
      await this.wakeLockSentinel.release();
    } catch (error) {
      console.debug("Wake lock release failed", error);
    } finally {
      this.wakeLockSentinel = null;
    }
  }

  handleWakeLockRelease() {
    if (!this.wakeLockSentinel) return;
    this.wakeLockSentinel.removeEventListener(
      "release",
      this.handleWakeLockRelease
    );
    this.wakeLockSentinel = null;
    if (this.state.isRecording && this.isDocumentVisible()) {
      this.scheduleWakeLockRetry();
    }
  }

  handleWakeLockVisibilityChange() {
    if (!this.state.isRecording) return;
    if (this.isDocumentVisible()) {
      this.acquireWakeLock();
    }
  }

  scheduleWakeLockRetry(delay = 1500) {
    if (!this.state.isRecording) return;
    if (this.wakeLockRetryTimeout) return;
    if (!this.isDocumentVisible()) return;
    this.wakeLockRetryTimeout = setTimeout(() => {
      this.wakeLockRetryTimeout = null;
      if (this.state.isRecording && this.isDocumentVisible()) {
        this.acquireWakeLock();
      }
    }, delay);
  }

  clearWakeLockRetry() {
    if (this.wakeLockRetryTimeout) {
      clearTimeout(this.wakeLockRetryTimeout);
      this.wakeLockRetryTimeout = null;
    }
  }

  isDocumentVisible() {
    if (typeof document === "undefined") return true;
    return document.visibilityState === "visible";
  }

  async mixAudioTracks(tracks = []) {
    const validTracks = tracks.filter((track) => Boolean(track));
    if (!validTracks.length) return null;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const useFallbackMix = !AudioCtx;

    if (useFallbackMix) {
      const fallbackStream = new MediaStream();
      validTracks.forEach((track) => fallbackStream.addTrack(track));
      return fallbackStream;
    }

    try {
      if (!this.audioContext || this.audioContext.state === "closed") {
        this.audioContext = new AudioCtx();
      }

      if (this.audioSources.length) {
        this.audioSources.forEach(({ source }) => {
          try {
            source.disconnect();
          } catch (error) {
            console.debug("Audio source disconnect failed", error);
          }
        });
        this.audioSources = [];
      }

      this.mixedAudioDestination =
        this.audioContext.createMediaStreamDestination();

      validTracks.forEach((track) => {
        const stream = new MediaStream([track]);
        const source = this.audioContext.createMediaStreamSource(stream);
        source.connect(this.mixedAudioDestination);
        this.audioSources.push({ source, stream });
      });

      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume().catch(() => {});
      }

      this.audioContext.onstatechange = () => {
        if (
          this.audioContext &&
          this.audioContext.state === "suspended" &&
          this.state.isRecording &&
          !this.state.isPause
        ) {
          this.audioContext.resume().catch(() => {});
        }
      };

      return this.mixedAudioDestination.stream;
    } catch (error) {
      console.warn("Falling back to direct audio mix", error);
      const fallbackStream = new MediaStream();
      validTracks.forEach((track) => fallbackStream.addTrack(track));
      return fallbackStream;
    }
  }

  setupMediaRecorder(stream) {
    const mimeType = `video/${this.state.mime}`;
    const options = MediaRecorder.isTypeSupported(mimeType)
      ? { mimeType, videoBitsPerSecond: 3500000 }
      : { videoBitsPerSecond: 3500000 };
    this.set.mediaRecorder = new MediaRecorder(stream, options);

    this.set.mediaRecorder.ondataavailable = async (event) => {
      if (!event.data?.size) return;
      if (this.currentSessionId && !this.storageFailed) {
        const chunkIndex = this.chunkIndex;
        this.chunkIndex += 1;
        const writePromise = recordingStorage
          .appendChunk(this.currentSessionId, chunkIndex, event.data)
          .catch((error) => {
            console.warn(
              "IndexedDB chunk write failed; falling back to memory",
              error
            );
            this.storageFailed = true;
            this.recordedChunks.push(event.data);
          });
        this.pendingChunkWrites.push(writePromise);
        try {
          await writePromise;
          return;
        } catch (error) {
          console.debug("Chunk write recovery handled", error);
        }
      }
      this.recordedChunks.push(event.data);
    };

    this.set.mediaRecorder.onstop = async () => {
      await this.bakeVideo();
    };

    this.set.mediaRecorder.start(2000);
  }

  pauseRecording() {
    if (!this.set.mediaRecorder || this.state.isPause) return;
    this.set.mediaRecorder.pause();
    this.state.isPause = true;
    this.appendStatusNotification("pause");
    this.set.pauseAndResume?.classList.add("resume");
    this.set.pauseAndResume?.classList.remove("pause");
    this.updatePauseResumeLabel("Riprendi");

    const currentTimerVal = this.set.recordingTimer?.textContent || "00:00:00";
    document.title = `⏸️ PAUSE [${currentTimerVal}] - ScreenREC`;
  }

  resumeRecording() {
    if (!this.set.mediaRecorder || !this.state.isPause) return;
    this.set.mediaRecorder.resume();
    this.state.isPause = false;
    this.appendStatusNotification("resume");
    this.set.pauseAndResume?.classList.remove("resume");
    this.set.pauseAndResume?.classList.add("pause");
    this.updatePauseResumeLabel("Pausa");

    const currentTimerVal = this.set.recordingTimer?.textContent || "00:00:00";
    document.title = `🔴 REC [${currentTimerVal}] - ScreenREC`;
  }

  stopRecording() {
    this.stopAutoSwitchTimer();
    this.stopRecordingTimer();
    this.stopAutoSwitchCountdown();
    this.state.isRecording = false;

    this.disableWakeLockHandling();

    window.removeEventListener("beforeunload", this.beforeUnloadHandler);

    if (this.set.mediaRecorder && this.set.mediaRecorder.state !== "inactive") {
      this.set.mediaRecorder.stop();
    }

    this.mixEngine.stop();
    this.mixEngine.dispose();
    this.streamManager.stopAll();
    this.cleanupAudio();

    if (this.set.preview) {
      this.set.preview.srcObject = null;
      this.set.preview.controls = false;
      this.set.preview.muted = true;
      this.set.preview.classList.add("visible");
    }

    // Hide recording info and controls
    this.set.recordingInfo?.classList.remove("visible");
    this.set.recordingControls?.classList.remove("visible");
    this.set.pauseAndResume?.classList.remove("visible");
    this.set.pauseAndResume?.classList.remove("resume");
    this.set.stop?.classList.remove("visible");
    this.set.recordingSwitch?.style.setProperty("display", "none");
    this.updatePauseResumeLabel("Pausa");

    // Hide configuration UI while reviewing
    this.set.logo?.classList.add("force-hidden");
    this.set.headerText?.classList.add("force-hidden");
    this.set.choice?.classList.add("force-hidden");
    this.set.mimeChoiceWrapper?.classList.add("force-hidden");
    this.set.mimeChoiceWrapper?.classList.remove("visible");
    this.set.microphoneStudio?.classList.add("hidden", "force-hidden");
    this.set.cameraStudio?.classList.add("hidden", "force-hidden");
    this.set.start?.classList.add("force-hidden");
    this.set.footer?.classList.add("force-hidden");

    // Show only video and review controls (save + discard)
    if (this.set.reviewControls) {
      this.set.reviewControls.classList.add("visible");
      this.set.reviewControls.classList.remove("force-hidden");
      this.set.reviewControls.style.removeProperty("display");
      this.set.reviewControls.setAttribute("aria-hidden", "false");
    }
    if (this.set.discardButton) {
      this.set.discardButton.removeAttribute("disabled");
      this.set.discardButton.classList.add("visible");
    }
    if (this.set.download) {
      this.set.download.classList.add("visible");
      this.set.download.setAttribute("disabled", "true");
    }

    this.appendStatusNotification("stop");

    if (this.state.selectedCameras.length) {
      this.prepareCameraPreviews().catch((error) => {
        console.warn("Impossibile ripristinare le anteprime camera", error);
      });
    }
  }

  startRecordingTimer() {
    this.recordingStartTime = Date.now();
    this.originalTitle =
      document.title || "ScreenREC - A simple web screen recorder";
    if (this.set.recordingTimer) {
      this.set.recordingTimer.textContent = "00:00:00";
    }

    this.recordingTimerInterval = setInterval(() => {
      if (!this.state.isRecording || this.state.isPause) return;

      const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000);
      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor((elapsed % 3600) / 60);
      const seconds = elapsed % 60;

      const timeString = [hours, minutes, seconds]
        .map((n) => String(n).padStart(2, "0"))
        .join(":");

      if (this.set.recordingTimer) {
        this.set.recordingTimer.textContent = timeString;
      }

      document.title = `🔴 REC [${timeString}] - ScreenREC`;
    }, 1000);
  }

  stopRecordingTimer() {
    if (this.recordingTimerInterval) {
      clearInterval(this.recordingTimerInterval);
      this.recordingTimerInterval = null;
    }
    this.recordingStartTime = null;
    if (this.originalTitle) {
      document.title = this.originalTitle;
    }
  }

  startAutoSwitchCountdown() {
    this.stopAutoSwitchCountdown();

    if (!this.state.autoSwitchEnabled || !this.set.recordingSwitch) {
      return;
    }

    this.set.recordingSwitch.style.removeProperty("display");
    this.refreshAutoSwitchCountdownDisplay();

    this.autoSwitchCountdownInterval = setInterval(() => {
      if (!this.state.isRecording) return;
      if (this.state.isPause) return;
      this.refreshAutoSwitchCountdownDisplay();
    }, 500);
  }

  stopAutoSwitchCountdown() {
    if (this.autoSwitchCountdownInterval) {
      clearInterval(this.autoSwitchCountdownInterval);
      this.autoSwitchCountdownInterval = null;
    }
    if (this.set.recordingSwitch) {
      this.set.recordingSwitch.style.setProperty("display", "none");
    }
    if (this.set.recordingSwitchValue) {
      this.set.recordingSwitchValue.textContent = "--:--";
    }
  }

  getAutoSwitchRemainingMs() {
    if (
      this.state.autoSwitchMode === "spotlight" &&
      this.state.autoSwitchSpotlightEndsAt
    ) {
      return Math.max(0, this.state.autoSwitchSpotlightEndsAt - Date.now());
    }
    if (this.state.autoSwitchTimerEndsAt) {
      return Math.max(0, this.state.autoSwitchTimerEndsAt - Date.now());
    }
    return null;
  }

  refreshAutoSwitchCountdownDisplay() {
    if (!this.set.recordingSwitchValue) return;
    const remainingMs = this.getAutoSwitchRemainingMs();
    if (remainingMs === null || Number.isNaN(remainingMs)) {
      this.set.recordingSwitchValue.textContent = "--:--";
      return;
    }

    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    this.set.recordingSwitchValue.textContent = `${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  async bakeVideo() {
    if (this.pendingChunkWrites.length) {
      await Promise.allSettled(this.pendingChunkWrites);
      this.pendingChunkWrites = [];
    }

    let chunks = this.recordedChunks;

    if (this.currentSessionId) {
      const storedChunks = await recordingStorage.getSessionChunks(
        this.currentSessionId
      );
      chunks = [...storedChunks, ...this.recordedChunks];
      await recordingStorage.markSessionCompleted(this.currentSessionId);
    }

    if (!chunks.length) {
      this.appendStatusNotification("error", "Registrazione non valida");
      return;
    }

    const blob = new Blob(chunks, {
      type: `video/${this.state.mime}`,
    });
    const savedName = this.state.filename || this.getRandomString(15);
    const url = URL.createObjectURL(blob);
    this.lastRecordingUrl = url;

    if (this.set.download) {
      this.set.download.href = url;
      this.set.download.download = `${savedName}.${this.state.mime}`;
      this.set.download.classList.add("visible");
      this.set.download.removeAttribute("disabled");
    }

    if (this.set.preview) {
      this.set.preview.autoplay = false;
      this.set.preview.srcObject = null;
      this.set.preview.src = url;
      this.set.preview.controls = true;
      this.set.preview.muted = false;
      this.set.preview.classList.add("visible");
    }

    if (this.set.reviewControls) {
      this.set.reviewControls.classList.add("visible");
      this.set.reviewControls.style.removeProperty("display");
      this.set.reviewControls.setAttribute("aria-hidden", "false");
    }

    if (this.set.discardButton) {
      this.set.discardButton.removeAttribute("disabled");
      this.set.discardButton.classList.add("visible");
    }
  }

  discardRecording() {
    // Clear recorded data
    this.recordedChunks = [];
    if (this.lastRecordingUrl) {
      URL.revokeObjectURL(this.lastRecordingUrl);
      this.lastRecordingUrl = null;
    }

    // Clear video preview
    if (this.set.preview) {
      if (this.set.preview.src && this.set.preview.src.startsWith("blob:")) {
        URL.revokeObjectURL(this.set.preview.src);
      }
      this.set.preview.removeAttribute("src");
      this.set.preview.srcObject = null;
      this.set.preview.controls = false;
      this.set.preview.muted = true;
      this.set.preview.autoplay = true;
      this.set.preview.classList.remove("visible");
    }

    if (this.set.download) {
      if (
        this.set.download.href &&
        this.set.download.href.startsWith("blob:")
      ) {
        URL.revokeObjectURL(this.set.download.href);
      }
      this.set.download.href = "";
      this.set.download.removeAttribute("download");
      this.set.download.classList.remove("visible");
      this.set.download.setAttribute("disabled", "true");
    }
    if (this.set.discardButton) {
      this.set.discardButton.setAttribute("disabled", "true");
      this.set.discardButton.classList.remove("visible");
    }

    // Hide review controls
    if (this.set.reviewControls) {
      this.set.reviewControls.classList.remove("visible");
      this.set.reviewControls.setAttribute("aria-hidden", "true");
      this.set.reviewControls.style.setProperty("display", "none");
    }

    // Show all config UI elements again
    this.set.logo?.classList.remove("force-hidden");
    this.set.headerText?.classList.remove("force-hidden");
    this.set.choice?.classList.remove("force-hidden");
    // Restore format selector visibility (captureMode is already chosen)
    this.set.mimeChoiceWrapper?.classList.remove("force-hidden");
    this.set.mimeChoiceWrapper?.classList.add("visible");
    this.set.footer?.classList.remove("force-hidden");

    // Show studios and start button only if filename is present
    const filenameInput = document.getElementById("filename");
    const hasFilename = filenameInput?.value.trim().length > 0;
    if (hasFilename) {
      this.set.microphoneStudio?.classList.remove("hidden", "force-hidden");
      this.set.cameraStudio?.classList.remove("hidden", "force-hidden");
      this.set.start?.classList.remove("force-hidden");
    }

    // Restore camera previews if needed
    if (this.state.selectedCameras.length) {
      this.prepareCameraPreviews().catch((error) => {
        console.warn("Impossibile ripristinare le anteprime camera", error);
      });
    }

    this.appendStatusNotification("info", "Registrazione scartata");
  }

  cleanupAudio() {
    if (this.audioSources?.length) {
      this.audioSources.forEach(({ source }) => {
        try {
          source.disconnect();
        } catch (error) {
          console.debug("Audio source disconnect during cleanup failed", error);
        }
      });
      this.audioSources = [];
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.mixedAudioDestination = null;
  }

  getRandomString(length) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    return Array.from(
      { length },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join("");
  }

  appendStatusNotification(actionType, customMessage = "") {
    if (this.toastTimeout) clearTimeout(this.toastTimeout);

    const notificationText =
      actionType === "start"
        ? "Registrazione avviata"
        : actionType === "stop"
        ? "Registrazione terminata"
        : actionType === "pause"
        ? "Registrazione in pausa"
        : actionType === "resume"
        ? "Registrazione ripresa"
        : actionType === "error"
        ? customMessage || "Errore nella registrazione"
        : "";

    this.set.toast.className = "";
    this.set.toast.classList.add(
      actionType === "stop"
        ? "success"
        : actionType === "pause"
        ? "warning"
        : actionType === "error"
        ? "error"
        : "active"
    );

    document.getElementById("desc").innerHTML =
      notificationText || customMessage;

    const closeBtn = this.set.toast.querySelector(".toast-close");
    if (closeBtn) {
      closeBtn.onclick = () => {
        this.set.toast.classList.add("closing");
        setTimeout(
          () => this.set.toast.classList.remove("active", "closing"),
          500
        );
      };
    }

    this.toastTimeout = setTimeout(() => {
      this.set.toast.classList.remove("active");
    }, 3500);
  }

  cycleActiveCamera() {
    if (!this.state.selectedCameras.length) return;
    this.layoutManager.cycleCamera(this.state.selectedCameras.length);
    this.renderCameraPreviewGrid();
    if (this.state.autoSwitchEnabled) {
      this.restartAutoSwitchCycle({ preserveActive: true });
    }
  }

  startAutoSwitchTimer({ preserveActive = false } = {}) {
    this.restartAutoSwitchCycle({ preserveActive });
  }

  restartAutoSwitchCycle({ preserveActive = false } = {}) {
    const wasSpotlight = this.state.autoSwitchMode === "spotlight";
    const currentSecondary = this.state.autoSwitchCurrentSecondary;

    this.stopAutoSwitchTimer({ resetState: !preserveActive });

    if (!this.state.autoSwitchEnabled || !this.hasSecondaryCameras()) {
      return;
    }

    this.ensurePrimaryCamera();

    if (preserveActive && wasSpotlight && currentSecondary) {
      this.state.autoSwitchMode = "spotlight";
      this.state.autoSwitchCurrentSecondary = currentSecondary;
      this.startSpotlightCountdown();
      return;
    }

    if (!preserveActive) {
      this.clearSpotlightState();
      this.activatePrimaryCamera();
    }

    this.scheduleNextAutoSwitch();
  }

  scheduleNextAutoSwitch() {
    this.clearAutoSwitchTimer();
    if (!this.state.autoSwitchEnabled || !this.hasSecondaryCameras()) {
      return;
    }

    const delay = this.getRandomAutoSwitchDelayMs();
    this.state.autoSwitchMode = "waiting";
    this.state.autoSwitchTimerEndsAt = Date.now() + delay;
    this.state.autoSwitchTimer = setTimeout(() => {
      this.state.autoSwitchTimer = null;
      this.state.autoSwitchTimerEndsAt = null;
      this.beginAutoSwitchSpotlight();
    }, delay);
    this.refreshAutoSwitchCountdownDisplay();
  }

  beginAutoSwitchSpotlight() {
    if (!this.state.autoSwitchEnabled || !this.hasSecondaryCameras()) {
      this.clearSpotlightState();
      return;
    }

    const nextSecondary = this.pickNextSecondaryCamera();
    if (!nextSecondary) {
      this.scheduleNextAutoSwitch();
      return;
    }

    if (!this.enterSpotlightFor(nextSecondary)) {
      this.scheduleNextAutoSwitch();
    }
  }

  enterSpotlightFor(deviceId) {
    const index = this.state.selectedCameras.indexOf(deviceId);
    if (index === -1) {
      return false;
    }

    this.state.autoSwitchCurrentSecondary = deviceId;
    this.state.autoSwitchMode = "spotlight";
    this.layoutManager.setActiveCameraIndex(index);
    this.renderCameraPreviewGrid();
    this.startSpotlightCountdown();
    return true;
  }

  startSpotlightCountdown() {
    this.clearSpotlightTimer();
    if (!this.state.autoSwitchCurrentSecondary) {
      return;
    }

    const durationSeconds = Math.max(
      5,
      Number(this.state.autoSwitchSpotlightSeconds) || 0
    );
    const durationMs = durationSeconds * 1000;

    this.state.autoSwitchSpotlightEndsAt = Date.now() + durationMs;
    this.state.autoSwitchSpotlightTimer = setTimeout(() => {
      this.state.autoSwitchSpotlightTimer = null;
      this.state.autoSwitchSpotlightEndsAt = null;
      this.finishSpotlightCycle();
    }, durationMs);
    this.refreshAutoSwitchCountdownDisplay();
  }

  finishSpotlightCycle() {
    if (!this.state.autoSwitchEnabled) {
      this.clearSpotlightState();
      return;
    }

    this.state.autoSwitchLastSecondary = this.state.autoSwitchCurrentSecondary;
    this.clearSpotlightState();
    this.activatePrimaryCamera();
    this.scheduleNextAutoSwitch();
  }

  stopAutoSwitchTimer({ resetState = true } = {}) {
    this.clearAutoSwitchTimer();
    this.clearSpotlightTimer();
    if (resetState) {
      this.clearSpotlightState();
      this.state.autoSwitchLastSecondary = null;
    }
  }

  clearAutoSwitchTimer() {
    if (this.state.autoSwitchTimer) {
      clearTimeout(this.state.autoSwitchTimer);
      this.state.autoSwitchTimer = null;
    }
    this.state.autoSwitchTimerEndsAt = null;
    this.refreshAutoSwitchCountdownDisplay();
  }

  clearSpotlightTimer() {
    if (this.state.autoSwitchSpotlightTimer) {
      clearTimeout(this.state.autoSwitchSpotlightTimer);
      this.state.autoSwitchSpotlightTimer = null;
    }
    this.state.autoSwitchSpotlightEndsAt = null;
    this.refreshAutoSwitchCountdownDisplay();
  }

  clearSpotlightState() {
    this.state.autoSwitchMode = "idle";
    this.state.autoSwitchCurrentSecondary = null;
  }

  activatePrimaryCamera() {
    const primaryIndex = this.getPrimaryCameraIndex();
    if (primaryIndex >= 0) {
      this.layoutManager.setActiveCameraIndex(primaryIndex);
      this.renderCameraPreviewGrid();
    }
  }

  hasSecondaryCameras() {
    return this.state.selectedCameras.length > 1;
  }

  pickNextSecondaryCamera() {
    if (!this.hasSecondaryCameras()) return null;

    const candidates = this.state.selectedCameras.filter(
      (deviceId) => deviceId !== this.state.primaryCameraId
    );

    if (!candidates.length) return null;

    if (candidates.length === 1) {
      return candidates[0];
    }

    const withoutLast = candidates.filter(
      (deviceId) => deviceId !== this.state.autoSwitchLastSecondary
    );
    const pool = withoutLast.length ? withoutLast : candidates;
    const randomIndex = Math.floor(Math.random() * pool.length);
    return pool[randomIndex];
  }

  getRandomAutoSwitchDelayMs() {
    const minMinutes =
      Number(this.state.autoSwitchMinMinutes) || AUTO_SWITCH_MIN_DEFAULT;
    const maxMinutes =
      Number(this.state.autoSwitchMaxMinutes) || AUTO_SWITCH_MAX_DEFAULT;
    const clampedMin = Math.max(1, Math.min(minMinutes, maxMinutes));
    const clampedMax = Math.max(clampedMin, maxMinutes);
    const minMs = clampedMin * 60000;
    const maxMs = clampedMax * 60000;
    const range = maxMs - minMs;
    if (range <= 0) {
      return minMs;
    }
    return minMs + Math.floor(Math.random() * range);
  }

  beforeUnloadHandler(event) {
    if (this.state.isRecording) {
      event.preventDefault();
      event.returnValue =
        "Registrazione in corso. Se chiudi la pagina, potresti perdere il video.";
      return event.returnValue;
    }
  }

  async checkRecoveryOnStartup() {
    // No-op
  }

  registerRecoveryControls() {
    // Disabled recovery controls
  }
}
